/**
 * Today — the dashboard you land on, and the clock-in flow behind it.
 * Route: /clock  ("Facial Clock-In Flow" 3a: dashboard → camera → receipt)
 * @file src/web/features/clock/pages/index.tsx
 *
 * @llm-rule WHEN: The signed-in person needs to start or end their day
 * @llm-rule AVOID: Deciding on-site vs off-site here — the server owns that verdict
 * @llm-rule NOTE: The camera does NOT open on arrival any more. You land on a
 *   dashboard — status, shift window, location, this week — and open the camera
 *   deliberately. Steps: dashboard → camera → receipt, plus the enrolment gate
 *   and the off-site reason sheet.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Skeleton, Textarea, toast } from '@bloomneo/uikit';
import { MapPin, PartyPopper, ShieldCheck } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { useGeolocation } from '../../../shared/hooks/useGeolocation';
import {
  DEFAULT_CAPABILITIES,
  OFFSITE_REASON_LABEL,
  canClockIn,
  formatClock,
  formatDuration,
  greeting,
  type CapabilityMap,
  type OrgRole,
} from '../../../shared/domain';
import { FaceScanner, type FaceScannerHandle } from '../../face/components/FaceScanner';
import { FaceCaptureError } from '../../face/lib/faceEngine';
import { FaceEnrollment } from '../components/FaceEnrollment';
import { ClockDashboard, type WeekDay } from '../components/ClockDashboard';
import { ClockCamera, ClockReceipt } from '../components/ClockCamera';
import { OffsiteSheet, type OffsiteSubmission } from '../components/OffsiteSheet';

interface Office {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  radiusM: number;
}

interface TodayStatus {
  state: 'NOT_CLOCKED_IN' | 'CLOCKED_IN' | 'CLOCKED_OUT';
  recordId: string | null;
  clockInAt: string | null;
  clockOutAt: string | null;
  kind: string | null;
  approvalStatus: string | null;
  locationName: string | null;
  distanceM: number | null;
  faceMatchScore: number | null;
  workedMinutes: number | null;
  weekMinutes: number;
}

interface Context {
  attendanceRequired: boolean;
  /** Approved leave covering today. A half day does not block the clock. */
  onLeaveToday: {
    id: string;
    type: string;
    dayPart: string;
    blocksClockIn: boolean;
    cancellationPending: boolean;
    until: string;
  } | null;
  faceEnrolled: boolean;
  faceProvider: string;
  today: TodayStatus;
  offices: Office[];
  nonWorkingDay: boolean;
  holidayName: string | null;
  holidayKind: string | null;
  workStartTime?: string;
  workEndTime?: string;
}

/** Haversine, mirrored client-side purely to preview the fence before submitting. */
function metresBetween(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const R = 6_371_008.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude));
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function ClockPage() {
  const { user } = useAuth();
  /** The company's delegation map — decides whether a Head is on the clock. */
  const [capabilities, setCapabilities] = useState<CapabilityMap>(DEFAULT_CAPABILITIES);
  const { request } = useAppRequest();
  const geo = useGeolocation(true);

  const scannerRef = useRef<FaceScannerHandle>(null);
  /** The enrolled reference photo, shown at the moment of matching. */
  const [enrolledFace, setEnrolledFace] = useState<string | null>(null);

  const [context, setContext] = useState<Context | null>(null);
  const [week, setWeek] = useState<WeekDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showOffsite, setShowOffsite] = useState(false);
  const [signOffNote, setSignOffNote] = useState('');
  const [workingAnyway, setWorkingAnyway] = useState(false);

  /**
   * Which of the three screens is showing. The camera used to be the landing
   * page; now you arrive at the dashboard and open the camera on purpose.
   */
  const [step, setStep] = useState<'dashboard' | 'camera' | 'done'>('dashboard');
  const [mode, setMode] = useState<'IN' | 'OUT'>('IN');
  const [receipt, setReceipt] = useState<{
    title: string;
    time: string;
    rows: Array<{ label: string; value: string }>;
    note?: string;
  } | null>(null);

  /** One timer for the whole page, so the clock ticks without re-fetching. */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    void request<{ capabilities: CapabilityMap }>('/api/org/capabilities').then((r) => {
      if (r.ok) setCapabilities(r.data.capabilities);
    });
  }, [request]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    const today = new Date();
    const [result, face, month] = await Promise.all([
      request<{ context: Context }>('/api/attendance/context'),
      request<{ image: string | null }>('/api/attendance/face/me'),
      request<{ days: WeekDay[] }>(
        `/api/attendance/calendar?year=${today.getFullYear()}&month=${today.getMonth() + 1}`,
      ),
    ]);

    if (result.ok) setContext(result.data.context);
    else toast.error(result.error);

    // A missing photo is not an error — accounts enrolled before photos were
    // kept still match on their descriptor.
    if (face.ok) setEnrolledFace(face.data.image);

    if (month.ok) {
      // The last seven days that actually have a record, newest first.
      const cutoff = new Date(today.getTime() - 7 * 86_400_000).toISOString().slice(0, 10);
      setWeek(
        [...month.data.days]
          .filter((d) => d.day.slice(0, 10) >= cutoff)
          .sort((a, b) => b.day.localeCompare(a.day))
          .slice(0, 5),
      );
    }

    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  // Preview the fence verdict so the button can say the right thing. The
  // server recomputes this and has the final say.
  const nearest = (() => {
    if (!geo.fix || !context?.offices.length) return null;
    let best = context.offices[0];
    let bestDistance = metresBetween(geo.fix, best);
    for (const office of context.offices.slice(1)) {
      const d = metresBetween(geo.fix, office);
      if (d < bestDistance) {
        best = office;
        bestDistance = d;
      }
    }
    return { office: best, distanceM: bestDistance, inside: bestDistance <= best.radiusM };
  })();

  /** Capture a descriptor, surfacing coaching rather than a stack trace. */
  /**
   * One capture, descriptor only.
   *
   * Clock-in deliberately does NOT keep the frame: the enrolment photo is the
   * one image on file, and every clock-in is measured against it. Storing a
   * picture per clock-in was tried and removed — it is a pile of surveillance
   * for a check the descriptor already performs.
   */
  async function grabFace(): Promise<number[] | null> {
    try {
      const result = await scannerRef.current!.capture();
      return result.descriptor;
    } catch (err) {
      toast.error(err instanceof FaceCaptureError ? err.message : 'Face capture failed');
      return null;
    }
  }

  /** Was this clock-in after the shift started? Drives the receipt wording. */
  function lateNow(): boolean {
    const start = context?.workStartTime;
    if (!start) return false;
    const [h, m] = start.split(':').map(Number);
    const t = new Date();
    return t.getHours() * 60 + t.getMinutes() > h * 60 + m;
  }

  async function clockIn(offsite?: OffsiteSubmission) {
    setBusy(true);
    try {
      const fix = geo.fix ?? (await geo.locate());
      if (!fix) {
        toast.error(geo.error ?? 'Location is required to clock in');
        return;
      }

      const descriptor = await grabFace();
      if (!descriptor) return;

      const result = await request<{ message: string; today: TodayStatus }>(
        '/api/attendance/clock-in',
        {
          method: 'POST',
          body: {
            latitude: fix.latitude,
            longitude: fix.longitude,
            accuracyM: fix.accuracyM,
            faceDescriptor: descriptor,
            ...(offsite ?? {}),
          },
        },
      );

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      const record = result.data.today;
      const offSite = record.kind === 'OFF_SITE';

      setReceipt({
        title: 'Clocked in',
        time: formatClock(record.clockInAt),
        rows: [
          {
            label: 'Method',
            value: record.faceMatchScore
              ? `Face · ${Math.round(record.faceMatchScore * 100)}% match`
              : 'Face · verified',
          },
          {
            label: 'Location',
            value: offSite
              ? `Off-site${offsite?.offsiteReason ? ` · ${OFFSITE_REASON_LABEL[offsite.offsiteReason] ?? ''}` : ''}`
              : (record.locationName ?? 'On site'),
          },
          { label: 'Status', value: lateNow() ? 'Late' : 'On time' },
        ],
        note: context?.workEndTime
          ? `Shift ends ${context.workEndTime} — remember to clock out.`
          : undefined,
      });

      setShowOffsite(false);
      setStep('done');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function clockOut() {
    setBusy(true);
    try {
      const descriptor = await grabFace();
      if (!descriptor) return;

      const fix = geo.fix;
      const result = await request<{ message: string }>('/api/attendance/clock-out', {
        method: 'POST',
        body: {
          faceDescriptor: descriptor,
          signOffNote,
          ...(fix ? { latitude: fix.latitude, longitude: fix.longitude } : {}),
        },
      });

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      setReceipt({
        title: 'Clocked out',
        time: now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
        rows: [
          { label: 'Method', value: 'Face · verified' },
          { label: 'Today', value: formatDuration(context?.today.workedMinutes ?? null) },
          { label: 'This week', value: formatDuration(context?.today.weekMinutes ?? 0) },
        ],
        note: 'That is the day on record. Go home.',
      });

      setSignOffNote('');
      setStep('done');
      await load();
    } finally {
      setBusy(false);
    }
  }

  const firstName = (user?.name ?? '').split(' ')[0] || 'there';

  if (loading) {
    return (
      <AppShell>
        <Skeleton className="h-[520px] w-full rounded-[18px]" />
      </AppShell>
    );
  }

  if (!context) {
    return (
      <AppShell title="Something went wrong">
        <Panel>
          <p className="text-[13.5px] text-ink-soft">
            Your attendance context could not be loaded.
          </p>
          <Button className="mt-4" onClick={() => void load()}>
            Try again
          </Button>
        </Panel>
      </AppShell>
    );
  }

  // Whether this rank is on the clock at all. Decided BEFORE the enrolment
  // gate: asking someone to enrol a face for a screen they will never use is
  // both confusing and a privacy ask nobody owes.
  const myRole = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;
  const noAttendance = !canClockIn(myRole, capabilities);

  // ---- Enrolment comes before anything else, for anyone who clocks in ------
  if (!context.faceEnrolled && !noAttendance) {
    return (
      <AppShell>
        <FaceEnrollment onEnrolled={() => void load()} />
      </AppShell>
    );
  }

  const { today } = context;
  const inside = nearest?.inside ?? false;
  const holiday = context.nonWorkingDay;

  /*
    Approved leave closes the day. The server refuses the clock-in either way —
    this only makes the screen say so BEFORE the tap, instead of opening the
    face camera and failing at the end of it.

    Someone who is already clocked in when leave gets approved keeps their
    button: they still need to clock OUT.
  */
  const onLeave = context.onLeaveToday;
  const leaveBlocks = Boolean(onLeave?.blocksClockIn) && today.state === 'NOT_CLOCKED_IN';

  // ---- Screen 3: the receipt ----------------------------------------------
  if (step === 'done' && receipt) {
    return (
      <AppShell>
        <ClockReceipt
          title={receipt.title}
          time={receipt.time}
          rows={receipt.rows}
          note={receipt.note}
          onDone={() => {
            setReceipt(null);
            setStep('dashboard');
          }}
        />
      </AppShell>
    );
  }

  // ---- Off-site: collect the reason, then match the face -------------------
  if (showOffsite && today.state === 'NOT_CLOCKED_IN') {
    return (
      <AppShell>
        <div className="flex flex-col gap-3.5">
          <FaceScanner ref={scannerRef} caption="hold still — matching face" height={180} />
          <OffsiteSheet
            officeName={nearest?.office.name ?? context.offices[0]?.name ?? null}
            distanceM={nearest?.distanceM ?? null}
            busy={busy}
            onBack={() => setShowOffsite(false)}
            onSubmit={(submission) => void clockIn(submission)}
          />
        </div>
      </AppShell>
    );
  }

  // ---- Screen 2: the camera ------------------------------------------------
  if (step === 'camera') {
    const out = mode === 'OUT';
    return (
      <AppShell>
        <ClockCamera
          scannerRef={scannerRef}
          now={now}
          title={out ? `Clock out, ${firstName}` : `Clock in, ${firstName}`}
          caption={out ? 'hold still — confirming it’s you' : 'centre your face in the oval'}
          actionLabel={out ? 'Clock out' : 'Clock in'}
          timingNote={
            out
              ? 'will close today’s record'
              : lateNow()
                ? 'will log as late'
                : 'will log as on time'
          }
          busy={busy}
          onCancel={() => setStep('dashboard')}
          onCapture={() => void (out ? clockOut() : clockIn())}
        >
          {/* The face this is measured against — the rule made concrete. */}
          {enrolledFace && (
            <div className="flex items-center gap-3 rounded-xl bg-paper-sunk px-3 py-2.5">
              <img
                src={enrolledFace}
                alt="Your enrolled face"
                className="size-10 flex-none rounded-lg border border-rule object-cover"
              />
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-ink">Matching against your photo</div>
                <div className="text-[11px] text-ink-faint">
                  Enrolled once. A Company Admin or Head can reset it if it needs redoing.
                </div>
              </div>
            </div>
          )}

          {out && (
            <div className="rounded-[14px] border border-rule p-3.5">
              <div className="mb-2 text-[12.5px] font-medium text-ink">
                Anything worth noting? (optional)
              </div>
              <Textarea
                value={signOffNote}
                onChange={(e) => setSignOffNote(e.target.value)}
                rows={2}
                placeholder="Shipped the geofence fix."
                className="resize-none rounded-xl border-rule text-[12.5px]"
              />
            </div>
          )}
        </ClockCamera>
      </AppShell>
    );
  }

  // ---- Screen 1: the dashboard --------------------------------------------
  const clockedIn = today.state === 'CLOCKED_IN';
  const done = today.state === 'CLOCKED_OUT';

  return (
    <AppShell>
      <ClockDashboard
        noAttendance={noAttendance}
        greetingLine={`${greeting()}, ${firstName}.`}
        subLine={
          noAttendance
            ? myRole === 'COMPANY_ADMIN'
              ? 'Attendance does not apply to your role — you run the company, not the clock.'
              : 'Your company has not put Heads on the clock. Nothing here is expected of you.'
            : leaveBlocks
              ? onLeave?.cancellationPending
                ? 'You are on approved leave. Your Head has not decided your request to call it off yet, so you cannot clock in.'
                : 'You are on approved leave today. To work today, ask your Head to call the leave off first.'
              : holiday
                ? `${context.holidayName} — nothing is expected today.`
              : context.attendanceRequired
                ? 'Your face is your badge.'
                : 'Attendance is optional for you — clock in only to put the day on record.'
        }
        now={now}
        state={today.state}
        shiftStart={context.workStartTime}
        shiftEnd={context.workEndTime}
        weekMinutes={today.weekMinutes}
        workedMinutes={today.workedMinutes}
        clockInAt={today.clockInAt}
        clockOutAt={today.clockOutAt}
        insideFence={nearest ? nearest.inside : null}
        fenceName={nearest?.office.name ?? null}
        distanceM={nearest?.distanceM ?? null}
        locating={geo.state === 'locating'}
        week={week}
        primaryLabel={
          noAttendance
            ? 'Attendance not applicable'
            : leaveBlocks
              ? 'On approved leave'
              : done
                ? 'Day complete'
              : clockedIn
                ? 'Clock out'
                : 'Clock in'
        }
        primaryHint={
          noAttendance
            ? myRole === 'COMPANY_ADMIN'
              ? 'Company Admins are not on the clock'
              : 'Heads are not on the clock in this company'
            : leaveBlocks
              ? onLeave?.cancellationPending
                ? 'Waiting on your Head to approve the call-off'
                : `On leave until ${onLeave?.until ?? 'today'} — ask your Head to call it off`
              : done
                ? 'Nothing more to do today'
                : clockedIn
                ? 'Tap — opens the face camera'
                : inside || !nearest
                  ? 'Tap — opens the face camera'
                  : 'You are outside the office — tap to give a reason'
        }
        disabled={
          noAttendance || done || busy || leaveBlocks || (!clockedIn && holiday && !workingAnyway)
        }
        onPrimary={() => {
          if (done) return;
          if (clockedIn) {
            setMode('OUT');
            setStep('camera');
            return;
          }
          // Outside the fence (or no office set): the reason comes first.
          if (nearest && !inside) {
            setShowOffsite(true);
            return;
          }
          setMode('IN');
          setStep('camera');
        }}
        banner={
          noAttendance ? null :
          <>
            {geo.error && (
              <div
                className="flex items-start gap-2.5 rounded-[14px] px-3.5 py-3"
                style={{
                  background: 'var(--state-bad-bg)',
                  border: '1px solid var(--state-bad-border)',
                }}
              >
                <MapPin className="mt-0.5 size-4 flex-none text-flag" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] leading-relaxed text-ink">{geo.error}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 h-7 rounded-full px-3 text-[11.5px]"
                    onClick={() => void geo.locate()}
                  >
                    Retry location
                  </Button>
                </div>
              </div>
            )}

            {holiday && !workingAnyway && !clockedIn && (
              <div
                className="flex flex-wrap items-center gap-3 rounded-[14px] px-4 py-3.5"
                style={{ background: 'var(--holiday-bg)', border: '1px solid var(--holiday-border)' }}
              >
                <PartyPopper className="size-5 flex-none" style={{ color: 'var(--holiday-ink)' }} />
                <div className="min-w-0 flex-1 text-[13px] leading-relaxed text-ink">
                  <b className="font-semibold">{context.holidayName}</b>
                  <span className="block text-ink-soft">
                    Clock-in is off today. Nothing is expected from you.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setWorkingAnyway(true)}
                  className="text-[12.5px] font-medium text-ink-faint underline underline-offset-4 hover:text-ink"
                >
                  I’m working today
                </button>
              </div>
            )}
          </>
        }
      />

      {/* Out-of-region escape hatch, for someone inside the fence who is leaving. */}
      {!clockedIn && !done && inside && (!holiday || workingAnyway) && (
        <button
          type="button"
          onClick={() => setShowOffsite(true)}
          className="mt-3 w-full text-center text-[13px] font-medium text-brand hover:underline"
        >
          I’m out of region today →
        </button>
      )}

      <div className="mt-4 flex items-start gap-2 border-t border-rule pt-3 text-[11.5px] leading-relaxed text-ink-ghost">
        <ShieldCheck className="mt-0.5 size-3.5 flex-none" />
        <p>
          {context.workStartTime && !holiday && (
            <>
              Working day {context.workStartTime}–{context.workEndTime}. Clocking in after{' '}
              {context.workStartTime} is flagged.{' '}
            </>
          )}
          {noAttendance
            ? 'Managers and employees always clock in. Whether Heads do is set by the Company Admin under Authorities.'
            : holiday
              ? 'Holidays and weekly offs are never counted against you.'
              : context.attendanceRequired
                ? 'Required for employees and managers.'
                : 'Optional for your role — nobody is counting your hours.'}
        </p>
      </div>
    </AppShell>
  );
}

export default function ClockRoute() {
  return (
    <AuthGuard>
      <ClockPage />
    </AuthGuard>
  );
}
